import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, PLATFORM_META } from "@/lib/api";

export default function History() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [platform, setPlatform] = useState("all");

    useEffect(() => {
        api.get("/repurpose/history").then(({ data }) => {
            setItems(data);
            setLoading(false);
        });
    }, []);

    const filtered = items.filter((it) => {
        const matchQ = !q || (it.source + " " + (it.source_title || "")).toLowerCase().includes(q.toLowerCase());
        const matchP = platform === "all" || it.outputs.some((o) => o.platform === platform);
        return matchQ && matchP;
    });

    return (
        <div className="p-6 sm:p-10 max-w-7xl mx-auto" data-testid="history-root">
            <div className="font-mono-ui text-xs tracking-[0.25em] uppercase text-zinc-500 mb-2">// HISTORY</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Everything you&apos;ve repurposed.</h1>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" strokeWidth={1.5} />
                    <Input
                        data-testid="history-search"
                        placeholder="Search…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="pl-9 h-11 bg-transparent border-white/15 focus:border-white text-white"
                    />
                </div>
                <Select value={platform} onValueChange={setPlatform}>
                    <SelectTrigger data-testid="history-platform-filter" className="w-full sm:w-56 h-11 bg-transparent border-white/15 text-white">
                        <SelectValue placeholder="Platform" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All platforms</SelectItem>
                        {Object.entries(PLATFORM_META).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="mt-8 border border-white/10">
                {loading ? (
                    <div className="p-10 text-zinc-500 font-mono-ui text-xs uppercase tracking-widest">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="font-display font-bold text-xl">Nothing here</div>
                        <p className="text-zinc-500 text-sm mt-1">Start repurposing to fill this page.</p>
                    </div>
                ) : (
                    filtered.map((it, i) => (
                        <Link
                            key={it.id}
                            data-testid={`history-item-${i}`}
                            to={`/app/result/${it.id}`}
                            className="flex items-center justify-between gap-6 p-5 hover:bg-white/5 transition-colors border-b border-white/10 last:border-b-0 group"
                        >
                            <div className="min-w-0 flex-1">
                                <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-zinc-500">
                                    {new Date(it.created_at).toLocaleString()} — {it.source_type}
                                </div>
                                <div className="mt-1 font-display font-semibold truncate">
                                    {it.source_title || (it.source_type === "youtube" ? it.source : it.source.slice(0, 100))}
                                </div>
                            </div>
                            <div className="hidden md:flex items-center gap-2 shrink-0">
                                {it.outputs.map((o) => (
                                    <span key={o.platform} className="font-mono-ui text-[10px] uppercase tracking-widest text-zinc-400 px-2 py-1 border border-white/10">
                                        {PLATFORM_META[o.platform]?.label || o.platform}
                                    </span>
                                ))}
                            </div>
                            <ArrowUpRight className="w-4 h-4 text-zinc-600 group-hover:text-white transition-colors" />
                        </Link>
                    ))
                )}
            </div>
        </div>
    );
}
